import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button, IconButton } from '@keytrade/components-button';
import { InputText } from '@keytrade/components-inputtext';
import { SelectDropdown } from '@keytrade/components-selectdropdown';
import { FormSpacing } from '@keytrade/components-formspacing';
import { Icon } from '@keytrade/components-icon';
import { Card } from '@keytrade/components-card';
import { Span } from '@keytrade/components-span';
import { config } from '@keytrade/functions';
import { LoaderSpinner } from '@keytrade/components-loader';

import Title from '@/components/Title';
import Paragraph from '@/components/Paragraph';
import Container from '@/components/Container';
import BlockWrapper from '@/components/BlockWrapper';
import { mediaQuery } from '@/utils/helpers';
import CurrencyFormatter from '@/formatters/CurrencyFormatter';
import useTranslations from '@/hooks/useTranslations';
import keyplanGraph from './keyplan-simulation-graph.svg';
import keyprivateGraph from './keyprivate-simulation-graph.svg';
import Disclaimer, { DisclaimerType } from '@/components/Disclaimer';

const { list: colors } = config.colors;
const { getFont } = config.fonts;

const A_LA_CARTE = 'a la carte';
const SUBSTAINABILITY_DEFAULT = 0;

type Props = {
  id: string;
  locale: 'en' | 'fr' | 'nl';
  simulationType: 'keyplan' | 'keyprivate';
  minHeight: string;
  disclaimer?: DisclaimerType;
};

type darkType = {
  dark: boolean;
};

type loadingType = {
  isLoading: boolean;
  minHeight: string;
};

interface Frequency {
  label: string;
  value: string | number;
  api: number;
}

interface Profile {
  label: string;
  value: string;
  api: string | number;
}

const TitleWrapper = styled.div`
  text-align: center;
  margin-bottom: 6rem;
`;

const SimulationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6.4rem;
  margin-bottom: 6rem;

  ${mediaQuery.md(`
    flex-direction: row;
    gap: 2.4rem;
  `)}

  > * {
    width: 100%;

    ${mediaQuery.md(`
      width: 50%;
    `)}
  }
`;

const FormContainer = styled.form``;

const FormWrapper = styled.div`
  ${mediaQuery.md(`
    margin-top: 3.2rem;
    padding-right: 2.4rem;
  `)}
`;

const CardWrapper = styled.div`
  position: relative;
  max-width: 400px;
  margin: 0 auto;
`;

const CardBackground = styled.div<darkType>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  transform: rotate(5deg);
  ${({ dark }) =>
    dark
      ? `
        background-color: ${colors.Black};
        opacity: .25;
      `
      : `background-image: linear-gradient(
        to bottom,
        ${colors.BlueLight},
        ${colors.BlueCloudy}
    )`};
  border-radius: 1rem;
`;

const CardInnerWrapper = styled.div<darkType>`
  border-radius: 10px;
  ${({ dark }) => dark && `background-color: ${colors.BlueMoon};`}

  > * {
    height: 100%;
  }
`;

const CTAWrapper = styled.div`
  text-align: center;
`;

const ButtonLeftWrapper = styled.div`
  position: absolute;
  top: 3.2rem;
  left: 3.2rem;
`;

const ButtonRightWrapper = styled.div`
  position: absolute;
  top: 3.2rem;
  right: 3.2rem;
`;

const CardHeader = styled.div`
  width: 100%;
  margin: 2.4rem 0;
  text-align: center;
`;

const CardSubtitle = styled.div<darkType>`
  margin-bottom: 0.8rem;
  font-size: 1.2rem;
  font-family: ${({ theme }) => getFont(theme, 'bold')};
  color: ${({ dark }) => (dark ? colors.BlueCloudy : colors.GreyBlue)};
  text-transform: uppercase;
`;

const SimulationDisclaimer = styled.div<darkType>`
  margin-bottom: 0.8rem;
  font-size: 1.2rem;
  font-family: ${({ theme }) => getFont(theme, 'bold')};
  color: ${({ dark }) => (dark ? colors.BlueCloudy : colors.GreyBlue)};
  text-transform: uppercase;
`;

const Graph = styled.img`
  height: auto;
  width: 100%;
`;

const CardBody = styled.div<loadingType>`
  padding: 3.2rem;
  text-align: center;
  min-height: ${({ minHeight }) => (minHeight ? minHeight : 'auto')};
  ${({ isLoading }) =>
    isLoading &&
    `
    display: flex;
    justify-content: center; 
    align-items: center;
    ${mediaQuery.md(`
      padding: 6.4rem;
    `)}
  `};
`;

const LargeText = styled.p<darkType>`
  font-size: 3.2rem;
  font-family: ${({ theme }) => getFont(theme, 'bold')};
  color: ${({ dark }) => (dark ? colors.White : colors.BlueDark)};
`;

const AnnualRate = styled.p`
  font-size: 2rem;
  font-family: ${({ theme }) => getFont(theme, 'bold')};
  color: ${colors.White};
`;

const keyplanSPKeyBase = 'keyplan_simulation.profile';
const keyprivateSPKeyBase = 'keyprivate_simulation.profile';
const keyplanSFKeyBase = 'keyplan_simulation.frequency';
const keyprivateSSKeyBase = 'keyprivate_simulation.sustainability';

const BaseSimulationBlock: React.FC<Props> = ({
  locale,
  simulationType,
  id,
  minHeight,
  disclaimer,
}) => {
  const { t, to, FormattedMessage } = useTranslations();
  const isKeyplanSimulation = simulationType === 'keyplan';
  const MIN_INITIAL_AMOUNT = isKeyplanSimulation ? 0 : 15000;
  const MIN_INVESTMENT = isKeyplanSimulation ? 25 : 0;

  const PROFILES: Profile[] = isKeyplanSimulation
    ? Array.from([
        to(`${keyplanSPKeyBase}.cautious`, 'cautious', { api: 'C' }),
        to(`${keyplanSPKeyBase}.balanced`, 'balanced', { api: 'B' }),
        to(`${keyplanSPKeyBase}.audacious`, 'audacious', { api: 'A' }),
        to(`${keyplanSPKeyBase}.a_la_carte`, 'a la carte', { api: 'M' }),
      ])
    : Array.from([
        to(`${keyprivateSPKeyBase}.very_defensive`, 'very defensive', {
          api: 1,
        }),
        to(`${keyprivateSPKeyBase}.defensive`, 'defensive', { api: 2 }),
        to(`${keyprivateSPKeyBase}.very_moderate`, 'very moderate', { api: 3 }),
        to(`${keyprivateSPKeyBase}.moderate`, 'moderate', { api: 4 }),
        to(`${keyprivateSPKeyBase}.balanced`, 'balanced', { api: 5 }),
        to(`${keyprivateSPKeyBase}.growth`, 'growth', { api: 6 }),
        to(`${keyprivateSPKeyBase}.dynamic`, 'dynamic', { api: 7 }),
        to(`${keyprivateSPKeyBase}.very_dynamic`, 'very dynamic', { api: 8 }),
        to(`${keyprivateSPKeyBase}.aggressive`, 'aggressive', { api: 9 }),
        to(`${keyprivateSPKeyBase}.very_aggressive`, 'very aggressive', {
          api: 10,
        }),
      ]);

  const FREQUENCIES: Frequency[] = isKeyplanSimulation
    ? Array.from([
        to(`${keyplanSFKeyBase}.month`, 'month', { api: 1 }),
        to(`${keyplanSFKeyBase}.quarter`, 'quarter', { api: 2 }),
        to(`${keyplanSFKeyBase}.semester`, 'semester', { api: 3 }),
        to(`${keyplanSFKeyBase}.year`, 'year', { api: 4 }),
      ])
    : Array.from({ length: 26 }, (_, i) => ({
        label: `${i + 5} ${t('years')}`,
        value: i + 5,
        api: i + 5,
      }));

  const SUSTAINABILITY = [
    to(`${keyprivateSSKeyBase}.yes`, 1, { api: 1 }),
    to(`${keyprivateSSKeyBase}.no`, 0, { api: 2 }),
  ];

  const [initialAmount, setInitialAmount] = useState(
    isKeyplanSimulation ? 0 : 25000,
  );
  const [recurringAmount, setRecurringAmount] = useState(MIN_INVESTMENT);
  const [frequency, setFrequency] = useState(
    isKeyplanSimulation ? 'quarter' : 10,
  );
  const [profile, setProfile] = useState(
    isKeyplanSimulation ? PROFILES[1] : PROFILES[6],
  );
  const [investedAmount, setInvestedAmount] = useState(0);
  const [earnedAmount, setEarnedAmount] = useState(0);
  const [estimatedAnnualRate, setEstimatedAnnualRate] = useState(0);
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sustainability, setSustainability] = useState(SUBSTAINABILITY_DEFAULT);
  const [errorSustainability, setErrorSustainability] = useState(false);

  const handleFormSubmit = (e, previewURL) => {
    e.preventDefault();
    window.location.href = previewURL;
  };

  useEffect(() => {
    if (initialAmount < MIN_INITIAL_AMOUNT) return;
    if (recurringAmount < MIN_INVESTMENT) return;
    if (profile.value === A_LA_CARTE) return;

    const fetchUrl = isKeyplanSimulation
      ? `${process.env.GATSBY_API_ORIGIN}/node/backend/cldprxy/tisa/keyplan/simulation`
      : `${
          process.env.GATSBY_API_ORIGIN
        }/node/backend/cldprxy/tisa/${simulationType}/simulation?initialAmount=${initialAmount}&monthlyAmount=${recurringAmount}&riskLevel=${
          profile.api
        }&targetDateInYears=${
          FREQUENCIES.find((el) => el.value === frequency).api
        }&sustainability=${sustainability}`;

    const fetchOptions = isKeyplanSimulation
      ? {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            profile: profile.api,
            initialAmount: initialAmount || 0,
            recurringAmount: recurringAmount,
            periodicity: FREQUENCIES.find((el) => el.value === frequency).api,
          }),
        }
      : {};

    setApiError(false);
    setIsLoading(true);

    fetch(fetchUrl, fetchOptions)
      .then((res) => {
        if (!res.ok) {
          setApiError(true);
        }

        return res.json();
      })
      .then((res) => {
        if (res) {
          if (isKeyplanSimulation) {
            setInvestedAmount(res.data.chartData.at(-1).Invested);
            setEarnedAmount(
              Math.round(res.data.chartData.at(-1).Wealth * 100) / 100,
            );

            return;
          }

          setEstimatedAnnualRate(
            Math.round(res.yearlyReturnInPercent.normal * 100) / 100,
          );
          setEarnedAmount(
            Math.round(res.lastEstimatedValues.normal * 100) / 100,
          );
        }
      })
      .finally(() => setIsLoading(false));
  }, [profile, initialAmount, recurringAmount, frequency, sustainability]);

  const previewPath = t(
    isKeyplanSimulation
      ? 'keyplan_simulation.path_fragment'
      : 'keyprivate_simulation.path_fragment',
  );

  const initialAmountQuery = `initialAmount=${initialAmount || 0}`;
  const periodicityQuery = FREQUENCIES.find((el) => el.value === frequency).api;

  const previewURL = isKeyplanSimulation
    ? `${process.env.GATSBY_API_ORIGIN}${previewPath}/?${initialAmountQuery}&recurringAmount=${recurringAmount}&periodicity=${periodicityQuery}&selectedProfile=${profile.api}`
    : `${process.env.GATSBY_API_ORIGIN}${previewPath}/?${initialAmountQuery}&riskLevel=${profile.api}&targetDateInYears=${periodicityQuery}&monthlyAmount=${recurringAmount}&sustainability=${sustainability}`;

  const today = new Date();
  const todayMonth = new Intl.DateTimeFormat(
    `${locale}-${locale.toUpperCase()}`,
    { month: 'long' },
  ).format(today);

  // This line is strange, need to dig it up
  const targetDateInYears = today.getFullYear() + frequency;

  return (
    <BlockWrapper id={id} color={isKeyplanSimulation ? 'White' : 'Blue Dark'}>
      <Container>
        <TitleWrapper>
          <Title
            size='xl'
            level='h2'
            margin='2rem 0'
            lightColor={simulationType !== 'keyplan'}
          >
            <FormattedMessage
              id={
                isKeyplanSimulation
                  ? 'keyplan_simulation.title'
                  : 'keyprivate_simulation.title'
              }
            />
          </Title>
          <Paragraph
            color={isKeyplanSimulation ? 'GreyBlue' : 'White'}
            weight='semiBold'
          >
            <FormattedMessage
              id={
                isKeyplanSimulation
                  ? 'keyplan_simulation.subtitle'
                  : 'keyprivate_simulation.subtitle'
              }
            />
          </Paragraph>
        </TitleWrapper>
      </Container>
      <Container narrow>
        <FormContainer>
          <SimulationWrapper>
            <FormWrapper>
              <FormSpacing>
                <InputText
                  type='money'
                  label={t(
                    isKeyplanSimulation
                      ? 'keyplan_simulation.starting_amount'
                      : 'keyprivate_simulation.starting_capital',
                  )}
                  suffix='EUR'
                  isOptional={isKeyplanSimulation}
                  language={locale}
                  value={initialAmount || ''}
                  onChange={(_, value) =>
                    setInitialAmount(Math.abs(parseInt(value, 10)) || 0)
                  }
                  errorMessage={
                    initialAmount < MIN_INITIAL_AMOUNT &&
                    t(
                      isKeyplanSimulation
                        ? 'keyplan_simulation.insufficient_investment'
                        : 'keyprivate_simulation.insufficient_investment',
                    )
                  }
                />
                <InputText
                  type='money'
                  label={t(
                    isKeyplanSimulation
                      ? 'keyplan_simulation.recurring_investment'
                      : 'keyprivate_simulation.monthly_deposit',
                  )}
                  suffix='EUR'
                  value={recurringAmount}
                  onChange={(_, value) =>
                    setRecurringAmount(Math.abs(parseInt(value, 10)) || 0)
                  }
                  errorMessage={
                    recurringAmount < MIN_INVESTMENT &&
                    t(
                      isKeyplanSimulation
                        ? 'keyplan_simulation.insufficient_investment'
                        : 'keyprivate_simulation.insufficient_investment',
                    )
                  }
                  language={locale}
                  isOptional={simulationType !== 'keyplan'}
                />
                <SelectDropdown
                  label={t(
                    isKeyplanSimulation
                      ? `${keyplanSFKeyBase}.label`
                      : 'keyprivate_simulation.investment_horizon',
                  )}
                  options={FREQUENCIES}
                  value={frequency}
                  onChange={(value) => {
                    setFrequency(value);
                  }}
                />
                <SelectDropdown
                  label={t(
                    isKeyplanSimulation
                      ? 'keyplan_simulation.profile.label'
                      : 'keyprivate_simulation.profile.label',
                  )}
                  options={PROFILES}
                  value={profile.value}
                  onChange={(value) => {
                    setProfile(
                      PROFILES.find((element) => element.value === value),
                    );
                  }}
                />
                {simulationType === 'keyprivate' && (
                  <SelectDropdown
                    label={t('keyprivate_simulation.sustainability.preference')}
                    options={SUSTAINABILITY}
                    value={sustainability}
                    onChange={(value) => setSustainability(value)}
                    errorMessage={
                      errorSustainability &&
                      t('keyprivate_simulation.sustainability.error')
                    }
                    language={locale}
                  />
                )}
              </FormSpacing>
            </FormWrapper>
            <CardWrapper>
              <CardBackground dark={simulationType !== 'keyplan'} />
              <Card fillContainer>
                <CardInnerWrapper dark={simulationType !== 'keyplan'}>
                  <ButtonLeftWrapper>
                    <IconButton
                      icon={<Icon icon='icn_arrowLeft' />}
                      kind='secondary'
                      noSubmit
                      onClick={() => {
                        const currentProfile = PROFILES.find(
                          (item) => item.api === profile.api,
                        );
                        const newProfile =
                          PROFILES.indexOf(currentProfile) === 0
                            ? PROFILES[PROFILES.length - 1]
                            : PROFILES[PROFILES.indexOf(currentProfile) - 1];
                        setProfile(newProfile);
                      }}
                    />
                  </ButtonLeftWrapper>
                  <CardHeader>
                    <CardSubtitle dark={simulationType !== 'keyplan'}>
                      {simulationType}
                    </CardSubtitle>
                    <Paragraph
                      weight='bold'
                      color={isKeyplanSimulation ? 'BlueDark' : 'White'}
                    >
                      {profile.label}
                    </Paragraph>
                  </CardHeader>
                  <ButtonRightWrapper>
                    <IconButton
                      icon={<Icon icon='icn_arrowRight' />}
                      kind='secondary'
                      noSubmit
                      onClick={() => {
                        const currentProfile = PROFILES.find(
                          (item) => item.api === profile.api,
                        );
                        const newProfile =
                          PROFILES.indexOf(currentProfile) ===
                          PROFILES.length - 1
                            ? PROFILES[0]
                            : PROFILES[PROFILES.indexOf(currentProfile) + 1];
                        setProfile(newProfile);
                      }}
                    />
                  </ButtonRightWrapper>
                  <Graph
                    src={isKeyplanSimulation ? keyplanGraph : keyprivateGraph}
                  />
                  <CardBody isLoading={isLoading} minHeight={minHeight}>
                    {isLoading ? (
                      <LoaderSpinner />
                    ) : (
                      <>
                        {apiError ? (
                          <Paragraph color='Tomato'>
                            {t('generic_error')}
                          </Paragraph>
                        ) : isKeyplanSimulation ? (
                          <>
                            {profile.value === A_LA_CARTE ? (
                              <>
                                <Paragraph size='sm' margin='0 0 1.6rem'>
                                  {t('keyplan_simulation.a_la_carte_preview')}
                                </Paragraph>
                                <Button href={previewURL}>
                                  {t('keyplan_simulation.cta_a_la_carte')}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Paragraph size='sm' margin='0 0 1.6rem'>
                                  {t('keyplan_simulation.preview_1', {
                                    date: '01/04/2014',
                                  })}
                                  <Span weight='bold'>{` ${investedAmount}EUR`}</Span>
                                </Paragraph>
                                <Paragraph size='sm' margin='0 0 1.6rem'>
                                  {t('keyplan_simulation.preview_2', {
                                    type: profile.label.toLocaleLowerCase(),
                                  })}
                                  <Span weight='bold'>{` ${earnedAmount}EUR`}</Span>
                                </Paragraph>
                                <Paragraph size='sm'>
                                  {t('keyplan_simulation.difference')}
                                </Paragraph>
                                <LargeText dark={false}>
                                  +
                                  <CurrencyFormatter>{`${
                                    Math.round(
                                      (earnedAmount - investedAmount) * 100,
                                    ) / 100
                                  }EUR`}</CurrencyFormatter>
                                </LargeText>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <Paragraph
                              size='sm'
                              margin='0 0 .4rem'
                              color='White'
                            >
                              {`${t(
                                'keyprivate_simulation.preview_1',
                              )} ${todayMonth} ${targetDateInYears}:`}
                            </Paragraph>
                            <LargeText dark>
                              <CurrencyFormatter>{`${earnedAmount}EUR`}</CurrencyFormatter>
                            </LargeText>
                            <Paragraph
                              size='sm'
                              color='White'
                              margin='2.6rem 0 0'
                            >
                              {t('keyprivate_simulation.preview_2')}
                            </Paragraph>
                            <AnnualRate>{estimatedAnnualRate}%</AnnualRate>
                          </>
                        )}
                      </>
                    )}
                  </CardBody>
                </CardInnerWrapper>
              </Card>
            </CardWrapper>
          </SimulationWrapper>
          <CTAWrapper>
            <Button
              size='lg'
              margin='0 0 2.4rem'
              onClick={(e) => handleFormSubmit(e, previewURL)}
            >
              {t('keyplan_simulation.cta')}
            </Button>
            {disclaimer && (
              <Disclaimer
                richtext={disclaimer.richtext}
                color={isKeyplanSimulation ? 'GreyBlue' : 'White'}
                padding='0'
                fontSize='1.4rem'
              />
            )}
          </CTAWrapper>
        </FormContainer>
      </Container>
    </BlockWrapper>
  );
};

export default BaseSimulationBlock;
